import { get } from 'lodash';
import { MoneyPrice } from '../../../core/money';
import {
  RoomImageMetadata,
  RoomOccupancy,
  RoomSummaryType,
  RoomTemplateImage,
} from './room.interface';
import { RoomModel } from './room.model';
import { RoomSearchModel } from './room-search.model';
import { Rate } from '../../rate/model';
import { RoomBasketModel, RoomBasketOccupancy } from './room-basket.model';
import { Language } from '../../../core';

const getDefaultOccupancy = (room: RoomModel) => {
  return room?.occupancies.find((occ) => !!occ.isDefault);
};
/**
 * Returns the desired occupancy. or fallsback to default if none provided.
 * @param room
 * @param occId
 */
const getOccupancy: {
  (room: RoomBasketModel, occId: number | string): RoomBasketOccupancy;
  (room: RoomModel, occId: number | string): RoomOccupancy;
} = (room: RoomModel | RoomBasketModel, occId: number | string) => {
  return Object.values(room.occupancies).find(
    (occ) => `${occ.occupancyId}` === `${occId}`
  );
};

const getCheapestRate = (room: RoomModel | RoomSearchModel) => {
  if (!room.rates) {
    return {} as Rate;
  }

  let indexOfMin = 0;
  for (let i = 1; i < room.rates.length; i++) {
    const amountToCompare = (index: number) =>
      room.rates[index].price.totalPrice.original.value.amount;
    if (amountToCompare(indexOfMin) > amountToCompare(i)) {
      indexOfMin = i;
    }
  }
  return room.rates[indexOfMin];
};

const getCheapestPriceFormatted = (room: RoomModel | RoomSearchModel) => {
  return getCheapestPrice(room).text;
};

const getCheapestPriceValue = (room: RoomModel | RoomSearchModel) => {
  return getCheapestPrice(room).value;
};

const getCheapestPrice = (room: RoomModel | RoomSearchModel): MoneyPrice => {
  return get(
    getCheapestRate(room),
    'price.totalPrice.converted',
    {} as MoneyPrice
  );
};

const getCheapestCrossedOutPriceFormatted = (
  room: RoomModel | RoomSearchModel
) => {
  return getCheapestCrossedOutPrice(room).text;
};

const getCheapestCrossedOutPrice = (
  room: RoomModel | RoomSearchModel
): MoneyPrice => {
  return get(
    getCheapestRate(room),
    'price.crossedOutPrice.converted',
    {} as MoneyPrice
  );
};

const getAvailabilityByRate = (rate: Rate) => {
  return (rate && rate.availableQuantity) || 0;
};

const getAvailabilityForCheapestRate = (room: RoomModel) => {
  return getAvailabilityByRate(getCheapestRate(room));
};

const getCoverImage = (room: RoomModel) => {
  if (room.pictures.length) {
    return room.pictures[0].templates.find(
      (t) => t.name === RoomTemplateImage.Original
    );
  }

  return {} as RoomImageMetadata;
};

const getCarouselImages = (room: RoomModel) => {
  if (room.pictures.length) {
    const roomTemplates = room.pictures.map((r) => r.templates);
    return roomTemplates.map((t) =>
      t.find((t) => t.name === RoomTemplateImage.Original)
    );
  }

  return [] as RoomImageMetadata[];
};

const getSummary = (room: RoomModel) => {
  return room.summary.find((s) => {
    if (s.value === RoomSummaryType.Html) {
      return s;
    } else if (s.value === RoomSummaryType.PlainText) {
      return s;
    }
    return;
  });
};

const getRoomBedsFormatted = (room: RoomModel) => {
  const bedDescriptions = room.bedding.beds.map(
    (bed) => `${bed.count} x ${bed.type.text}`
  );
  return bedDescriptions.reduce((acc, bedDesc, index) => {
    if (index === bedDescriptions.length - 1) {
      return acc + bedDesc;
    }
    return acc + bedDesc + ' ' + Language.getTranslation('or') + ' ';
  }, '');
};

export const RoomHelper = {
  getDefaultOccupancy,
  getOccupancy,
  getCheapestRate,
  getCheapestPrice,
  getCheapestPriceFormatted,
  getCoverImage,
  getCarouselImages,
  getAvailabilityForCheapestRate,
  getSummary,
  getCheapestCrossedOutPrice,
  getCheapestCrossedOutPriceFormatted,
  getRoomBedsFormatted,
  getCheapestPriceValue,
};
